import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/models/api-response';
import { ISupplierRequest, ISupplierResponse } from '../models/suppliers/supplier.model';

@Injectable({
  providedIn: 'root',
})
export class SupplierApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/suppliers`;
  }

  constructor(private http: HttpClient) {}

  getPagedSuppliers(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<ISupplierResponse[]>> {
    const params = createFilterParams(currentFilter, undefined, {
      favorite: 'isFavorite',
    });

    return this.http.get<ApiResponse<ISupplierResponse[]>>(this.url, { params });
  }

  getAllSuppliers(departmentId?: string): Observable<ISupplierResponse[]> {
    let url = `${this.url}`;
    if (departmentId) {
      url = `${url}/List?DepartmentId=${departmentId}`;
    }
    return this.http.get<ISupplierResponse[]>(url);
  }

  getSupplierById(
    id: string,
    departmentId?: string,
    toCurrencyCode?: string
  ): Observable<ISupplierResponse> {
    const url = `${this.url}/${id}`;
    let params = new HttpParams();
    if (departmentId) {
      params = params.append('DepartmentId', departmentId);
    }
    if (toCurrencyCode) {
      params = params.append('ToCurrencyCode', toCurrencyCode);
    }
    return this.http.get<ISupplierResponse>(url, { params });
  }

  createSupplier(supplier: ISupplierRequest): Observable<ISupplierResponse> {
    return this.http.post<ISupplierResponse>(this.url, supplier, { headers: this.headers });
  }

  updateSupplier(supplier: ISupplierRequest): Observable<ISupplierResponse> {
    const url = `${this.url}/${supplier.id}`;
    return this.http.put<ISupplierResponse>(url, supplier, { headers: this.headers });
  }

  deleteSupplier(id: string) {
    const url = `${this.url}/${id}`;
    return this.http.delete<ISupplierResponse>(url);
  }

  importProducts(id: string, file: File) {
    const url = `${this.url}/${id}/Import`;
    const formData = new FormData();
    formData.append('fileToImport', file, file.name);
    return this.http.post(url, formData);
  }

  exportProducts(id: string): Observable<Blob> {
    const url = `${this.url}/${id}/Export`;
    return this.http.get(url, { responseType: 'blob' });
  }

  importSuppliers(file: File, departments: string[], departmentGroups: string[]) {
    const url = `${this.url}/Import`;
    const formData = new FormData();
    formData.append('ExcelFile', file, file.name);
    departments.forEach((department, index) => {
      formData.append(`Departments[${index}]`, department);
    });
    departmentGroups.forEach((departmentGroup, index) => {
      formData.append(`DepartmentGroups[${index}]`, departmentGroup);
    });
    return this.http.post(url, formData);
  }

  favoriteSupplier(supplierId: string) {
    const url = `${this.url}/${supplierId}/Favorite`;
    return this.http.put(url, {});
  }

  unfavoriteSupplier(supplierId: string) {
    const url = `${this.url}/${supplierId}/Unfavorite`;
    return this.http.put(url, {});
  }
}
